/**
 * SourceSelectStep Component
 *
 * Allows selection of which project/customer to edit when multiple exist.
 */

import React from "react";
import classNames from "classnames";
import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Icon } from "@/components/ui/display/Icon";
import { generateId } from "@/lib/utils";
import { formatDecimal } from "@/utils/number/formatters";
import type { SourceSelectStepProps, SourceSelectionItem } from "../types";
import styles from "../EditForm.module.css";

/**
 * Format row hint text showing aggregated booking info
 * Format: "8T, 1 dag (8T/dag)" - matches Vue implementation
 */
const formatRowHint = (item: SourceSelectionItem): string => {
  return `${formatDecimal(item.totalHours)}T, ${item.bookingCount} dag (${formatDecimal(item.avgHours)}T/dag)`;
};

export const SourceSelectStep: React.FC<SourceSelectStepProps> = ({
  bookingType,
  items,
  selectedId,
  onSelectSource,
  onContinue,
  className,
  "data-testid": testId,
}) => {
  return (
    <div
      className={classNames(styles.container, className)}
      data-testid={testId}
    >
      <div
        className={styles.selectionStep}
        data-testid="edit-form-select-source"
      >
        <p className={styles.editHint}>
          {bookingType === "hard"
            ? "Valgte dager inneholder flere prosjekter. Velg prosjekt som skal endres."
            : "Valgte dager inneholder flere kunder. Velg kunde som skal endres."}
        </p>

        {items.map((item) => (
          <div
            key={item.id}
            className={classNames(styles.sourceRow, {
              [styles.selected]: selectedId === item.id,
              [styles.disabled]: selectedId !== item.id && selectedId != null,
            })}
            onClick={() => onSelectSource(item)}
            data-testid={`edit-form-source-${item.id}`}
          >
            <div className={styles.sourceRowName}>
              <Icon
                name={selectedId === item.id ? "edit" : "editOff"}
                size="md"
                className={styles.sourceRowIcon}
              />
              <span>
                {item.isAbsence
                  ? item.name
                  : item.code
                    ? `- ${item.code}`
                    : `- ${item.name}`}
              </span>
            </div>
            <div className={styles.sourceRowInfo}>
              <span className={styles.sourceRowHint}>
                {formatRowHint(item)}
              </span>
              <Checkbox
                id={generateId("source-select", item.id)}
                checked={selectedId === item.id}
                mode="radio"
                onChange={() => onSelectSource(item)}
              />
            </div>
          </div>
        ))}

        <div className={styles.actionsEnd}>
          <ActionButton
            label="Gå videre"
            icon="arrowRight"
            onClick={onContinue}
            disabled={selectedId == null}
            data-testid="edit-form-continue"
          />
        </div>
      </div>
    </div>
  );
};

SourceSelectStep.displayName = "SourceSelectStep";

export default SourceSelectStep;
